<template>
  <el-dialog
      v-model="visible"
      width="min(1280px, 94vw)"
      top="4vh"
      :show-close="false"
      :close-on-click-modal="false"
      destroy-on-close
      class="eml-preview-dialog"
  >
    <template #header="{ close }">
      <div class="eml-preview-dialog__toolbar">
        <span class="eml-preview-dialog__filename">{{ attachment?.name || t('common.eml.preview') }}</span>
        <button class="eml-preview-dialog__close" type="button" :aria-label="t('common.close')" @click="close">
          <el-icon><Close /></el-icon>
        </button>
      </div>
    </template>
    <article v-loading="loading" class="eml-preview">
      <template v-if="mail">
        <header class="eml-preview__header">
          <div class="eml-preview__message-meta">
            <h1>{{ mail.headers.subject || attachment?.name || '-' }}</h1>
            <dl class="eml-preview__recipient">
              <div>
                <dt>{{ t('common.eml.from') }}:</dt>
                <dd>{{ mail.headers.from || '-' }}</dd>
              </div>
              <div>
                <dt>{{ t('common.eml.to') }}:</dt>
                <dd>{{ mail.headers.to || '-' }}</dd>
              </div>
              <div v-if="mail.headers.cc">
                <dt>{{ t('common.cc') }}:</dt>
                <dd>{{ mail.headers.cc }}</dd>
              </div>
            </dl>
          </div>
          <time class="eml-preview__date">{{ formattedDate }}</time>
        </header>
        <div v-if="mail.attachments.length" class="eml-preview__attachments">
          <span class="eml-preview__attachments-label">{{ t('common.eml.attachments') }}</span>
          <span v-for="item in mail.attachments" :key="`${item.name}-${item.contentType}`" :title="item.contentType">{{ item.name }}</span>
        </div>
        <section class="eml-preview__body">
          <iframe
              v-if="mail.html"
              class="eml-preview__content"
              :srcdoc="safeHtml"
              sandbox="allow-popups"
              :title="t('common.eml.preview')"
          />
          <pre v-else class="eml-preview__text">{{ mail.text || '-' }}</pre>
        </section>
        <details v-if="mail.html && mail.text" class="eml-preview__plain-text">
          <summary>{{ t('common.eml.plain_text') }}</summary>
          <pre>{{ mail.text }}</pre>
        </details>
      </template>
      <el-empty v-else-if="errorMessage" :description="errorMessage" />
    </article>
  </el-dialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {Close} from '@element-plus/icons-vue';
import dayjs from 'dayjs';
import {parseEml} from '@/mail/parser/emlParser';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  attachment: {type: Object, default: null},
});
const emit = defineEmits(['update:modelValue']);
const {t} = useI18n();
const loading = ref(false);
const errorMessage = ref('');
const mail = ref(null);

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const formattedDate = computed(() => {
  const rawDate = mail.value?.headers.date || '';
  const date = dayjs(rawDate);
  return date.isValid() ? date.format('YYYY年MM月DD日 HH:mm:ss') : (rawDate || '-');
});

const sanitizeHtml = (html) => {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  doc.querySelectorAll('script, style, noscript, meta, base, link, form, object, embed, iframe').forEach((node) => node.remove());
  doc.querySelectorAll('*').forEach((node) => {
    [...node.attributes].forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim();
      if (name.startsWith('on') || (['href', 'src', 'action'].includes(name) && !/^(https?:|mailto:|tel:|cid:|data:image\/)/i.test(value))) {
        node.removeAttribute(attribute.name);
      }
    });
  });
  doc.querySelectorAll('a[href]').forEach((link) => {
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
  });
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;background:#fff;color:#1f2937;font:14px/1.6 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;word-break:break-word}body{padding:24px}img{max-width:100%!important;height:auto!important}table{max-width:100%!important}pre{white-space:pre-wrap;word-break:break-word}a{color:#1677ff}</style></head><body>${doc.body.innerHTML}</body></html>`;
};

const safeHtml = computed(() => {
  const inlineResources = mail.value?.inlineResources || {};
  const html = String(mail.value?.html || '').replace(/cid:([^"'\s>]+)/gi, (match, contentId) => (
    inlineResources[contentId.replace(/^<|>$/g, '').toLowerCase()] || ''
  ));
  return sanitizeHtml(html);
});

const loadEml = async () => {
  if (!props.attachment?.url) return;
  loading.value = true;
  errorMessage.value = '';
  mail.value = null;
  try {
    const url = props.attachment.url.replace(/^http:\/\//i, 'https://');
    const response = await fetch(url);
    if (!response.ok) throw new Error('request_failed');
    mail.value = parseEml(await response.text());
  } catch (error) {
    errorMessage.value = t('common.eml.load_failed');
  } finally {
    loading.value = false;
  }
};

watch(() => [props.modelValue, props.attachment?.url], ([isVisible]) => {
  if (isVisible) loadEml();
}, {immediate: true});
</script>

<style lang="less" scoped>
:deep(.eml-preview-dialog.el-dialog) { display: flex; flex-direction: column; height: 92vh; max-height: calc(100vh - 32px); margin: 4vh auto 0; padding: 0 !important; border-radius: 6px; }
:deep(.eml-preview-dialog .el-dialog__header) { flex: none; margin: 0 !important; padding: 0; border-bottom: 1px solid #edf0f3; }
:deep(.eml-preview-dialog .el-dialog__body) { flex: 1; min-height: 0; padding: 0; overflow: hidden; }
.eml-preview-dialog__toolbar { display: flex; height: 48px; align-items: center; justify-content: space-between; padding: 0 18px 0 24px; color: #1f2937; background: #fff; }
.eml-preview-dialog__filename { min-width: 0; overflow: hidden; font-size: 15px; font-weight: 600; line-height: 22px; text-overflow: ellipsis; white-space: nowrap; }
.eml-preview-dialog__close { display: grid; flex: none; width: 32px; height: 32px; padding: 0; place-items: center; color: #57606a; background: transparent; border: 0; border-radius: 4px; cursor: pointer; }
.eml-preview-dialog__close:hover { color: #1f2937; background: #f3f5f7; }
.eml-preview { height: 100%; min-height: 300px; overflow: auto; color: #1f2937; background: #fff; }
.eml-preview__header { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 24px; align-items: end; padding: 30px 32px 20px; border-bottom: 1px solid #e5e7eb; }
.eml-preview__message-meta { min-width: 0; }
.eml-preview__message-meta h1 { margin: 0 0 12px; color: #111827; font-size: 16px; font-weight: 600; line-height: 1.5; overflow-wrap: anywhere; }
.eml-preview__recipient { display: grid; gap: 4px; margin: 0; color: #6b7280; font-size: 13px; }
.eml-preview__recipient div { display: flex; min-width: 0; }
.eml-preview__recipient dt { flex: none; margin: 0 6px 0 0; color: #4b5563; font-weight: 400; }
.eml-preview__recipient dd { min-width: 0; margin: 0; overflow-wrap: anywhere; }
.eml-preview__date { max-width: 280px; color: #8a94a6; font-size: 13px; line-height: 20px; text-align: right; overflow-wrap: anywhere; }
.eml-preview__attachments { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 14px 32px; border-bottom: 1px solid #eef0f3; }
.eml-preview__attachments-label { color: #6b7280; font-size: 13px; }
.eml-preview__attachments span:not(.eml-preview__attachments-label) { max-width: 100%; padding: 5px 9px; color: #4b5563; font-size: 13px; background: #f3f4f6; border: 1px solid #e5e7eb; border-radius: 3px; overflow-wrap: anywhere; }
.eml-preview__body { min-height: 360px; background: #fff; }
.eml-preview__content { display: block; width: 100%; height: calc(92vh - 210px); min-height: 360px; border: 0; }
.eml-preview__text { min-height: 360px; margin: 0; padding: 24px 32px; white-space: pre-wrap; overflow-wrap: anywhere; background: #fff; font: 14px/1.7 inherit; }
.eml-preview__plain-text { margin: 14px 32px 0; border-top: 1px solid #eef0f3; color: #4b5563; }
.eml-preview__plain-text summary { padding: 12px 0; cursor: pointer; font-size: 13px; }
.eml-preview__plain-text pre { max-height: 240px; margin: 0; padding: 12px; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; background: #f9fafb; }
@media (max-width: 640px) { :deep(.eml-preview-dialog.el-dialog) { height: 96vh; margin-top: 2vh; border-radius: 0; } .eml-preview-dialog__toolbar { padding: 0 10px 0 16px; } .eml-preview__header { grid-template-columns: 1fr; gap: 8px; padding: 22px 16px 16px; } .eml-preview__date { max-width: none; text-align: left; } .eml-preview__attachments { padding-right: 16px; padding-left: 16px; } .eml-preview__content { height: calc(96vh - 220px); } .eml-preview__text { padding-right: 16px; padding-left: 16px; } .eml-preview__plain-text { margin-right: 16px; margin-left: 16px; } }
</style>
